/**
 * InventoryPanel — read-only stocking reference on the Scheduling dashboard.
 * Lists every supply item with its pack size and MAX (stock-to) quantity so
 * the team tops up to full at the start of each month. Counts and ordering
 * stay on the main /inventory page. Visible to all staff.
 *
 * Layout approved via /inventory-ref-mockup.html (2026-09-12).
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { getToken } from "@/lib/consult/authSession";

const NAVY = "#1F4E79";
const GREEN = "#0F3B2E";
const MUTED = "#6B7A73";
const BORDER = "#E5E2DC";
const RED = "#B4351B";

// The team's monthly stock list — exactly the items and order the owner
// provided (2026-09-12; second batch appended in the order given).
const REFERENCE_ORDER = [
  "HCV Kits", "HIV Kits", "Alcohol", "Bandage", "Cavi Wipes", "Gauze",
  "Gloves (L)", "Gloves (M)", "Gloves (XL)", "Lancets", "6 X 9\" Zipper Bags",
  "Black Containers", "DBS Envelopes", "Delivery Envelopes", "Labels",
  "Minivettes - 100 uL", "Xpert HCV", "Minivettes - 10 uL", "DBS",
];
// Per-item display tweaks: Black Containers ship 600/pack but the team only
// stocks 100 loose units (owner direction, 2026-09-12).
const DISPLAY_OVERRIDES = {
  blackcontainers: { maxText: "100", total: 100 },
};
const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "").replace("alchohol", "alcohol");

const api = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL });
api.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers["X-Auth-Token"] = t;
  return cfg;
});

const fmt = (n) => (n ?? 0).toLocaleString("en-CA");

export default function InventoryPanel({ refreshKey }) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setErr(false);
    api.get("/api/inventory/config")
      .then((r) => setItems(r.data.items || []))
      .catch(() => setErr(true));
  }, [refreshKey]);

  const [cat, setCat] = useState("");

  const ordered = useMemo(() => {
    if (!items) return null;
    const byName = new Map(items.map((it) => [norm(it.name), it]));
    return REFERENCE_ORDER.map((n) => byName.get(norm(n))).filter(Boolean);
  }, [items]);

  // Category filter buttons (same categories as the /inventory page).
  const categories = useMemo(() => {
    const seen = [];
    for (const it of ordered || []) {
      if (it.category && !seen.includes(it.category)) seen.push(it.category);
    }
    return seen;
  }, [ordered]);

  const visible = useMemo(
    () => (ordered || []).filter((it) => !cat || it.category === cat),
    [ordered, cat]
  );

  return (
    <div data-testid="inventory-panel">
      <style>{`
        .inv-ref-table { width: 100%; border-collapse: collapse; font-size: 12px; table-layout: fixed; }
        .inv-ref-table th { text-align: left; font-size: 9.5px; font-weight: 700; color: ${MUTED};
          text-transform: uppercase; letter-spacing: .08em; padding: 6px 3px; border-bottom: 1px solid ${BORDER};
          white-space: nowrap; background: #fff; }
        .inv-ref-table th.num, .inv-ref-table td.num { text-align: right; }
        .inv-ref-table td { padding: 8px 3px; border-bottom: 1px solid #F1F0EC; vertical-align: middle; }
        .inv-ref-table td:first-child { font-weight: 600; color: #1C2B26; }
        .inv-ref-units { display: inline-block; min-width: 28px; text-align: center; background: #F1F5F9;
          border-radius: 7px; padding: 2px 6px; font-weight: 600; color: #334155; }
        .inv-ref-max { font-weight: 800; color: ${NAVY}; font-size: 13px; }
        .inv-ref-total { font-weight: 700; color: ${GREEN}; }
      `}</style>

      {/* Same card + title style as the Calendar/MD tabs ("Staff member") so
          flipping between tabs is a clean transition. The 32px centered row
          matches the Calendar label row exactly (its label is vertically
          centred beside the share icon / name pill). */}
      <div className="outreach-card" style={{ border: `1px solid ${BORDER}`, borderRadius: 14, padding: 20, background: "white" }}>
        <div style={{ display: "flex", alignItems: "center", minHeight: 32 }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.24em", textTransform: "uppercase", color: "#2D5A4C" }}>
            Inventory
          </div>
        </div>
        <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2, whiteSpace: "nowrap" }}>
          Stock every item to its maximum each month.
        </div>

        {ordered !== null && !err && ordered.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 10 }} data-testid="inventory-cats">
            {["", ...categories].map((c) => (
              <button
                key={c || "all"}
                data-testid={c ? `inv-cat-${norm(c)}` : "inv-cat-all"}
                onClick={() => setCat(c)}
                style={{
                  borderRadius: 6, padding: "4px 8px", fontSize: 10, fontWeight: 700,
                  border: `1px solid ${cat === c ? NAVY : BORDER}`, cursor: "pointer",
                  background: cat === c ? NAVY : "#fff", color: cat === c ? "#fff" : "#2D5A4C",
                  fontFamily: "inherit", whiteSpace: "nowrap",
                }}
              >{c || "All"}</button>
            ))}
          </div>
        )}

        <div className="mt-2" style={{ background: "#fff", borderRadius: 12, border: `1px solid ${BORDER}`, padding: "0 8px", marginTop: 8 }}>
          {items === null && !err && (
            <div style={{ padding: "18px 4px", fontSize: 12, color: MUTED }} data-testid="inventory-loading">Loading…</div>
          )}
          {err && (
            <div style={{ padding: "18px 4px", fontSize: 12, color: RED, fontWeight: 600 }} data-testid="inventory-error">
              Couldn't load the inventory list — pull down to refresh.
            </div>
          )}
          {ordered !== null && !err && (
            <table className="inv-ref-table" data-testid="inventory-table">
              <thead>
                <tr>
                  <th style={{ width: "40%" }}>Item</th>
                  <th className="num" style={{ width: "18%" }}>Per pack</th>
                  <th className="num" style={{ width: "17%" }}>Max</th>
                  <th className="num" style={{ width: "25%" }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((it) => {
                  const ov = DISPLAY_OVERRIDES[norm(it.name)];
                  return (
                  <tr key={it.id} data-testid={`inventory-row-${it.id}`}>
                    <td>{it.name}</td>
                    <td className="num"><span className="inv-ref-units">{fmt(it.packQty)}</span></td>
                    <td className="num"><span className="inv-ref-max">{ov ? ov.maxText : fmt(it.max)}</span></td>
                    <td className="num inv-ref-total">{ov ? fmt(ov.total) : fmt((it.packQty || 0) * (it.max || 0))}</td>
                  </tr>
                  );
                })}
                {visible.length === 0 && (
                  <tr><td colSpan={4} style={{ padding: "18px 4px", color: MUTED }} data-testid="inventory-empty">No items in this category.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {visible.some((it) => norm(it.name).includes("glove")) && (
        <div style={{
          border: `1.5px solid ${RED}`, borderRadius: 12, padding: "12px 14px",
          background: "rgba(180,53,27,.04)", fontSize: 12, fontWeight: 700,
          color: RED, lineHeight: 1.45, marginTop: 10,
        }} data-testid="inventory-gloves-note">
          ⚠ Gloves — stock only the sizes required.
        </div>
        )}
      </div>
    </div>
  );
}
